import { Alert, ConfigProvider, Form, Input, Modal, Select } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4, validate as isUuid } from 'uuid';
import Button from 'components/common/Button';
import useCreateOrganisation from 'hooks/useCreateOrganisation';
import useUpdateOrganisation from 'hooks/useUpdateOrganisation';
import useFetchUserProfile from 'hooks/useFetchUserProfile';
import {
  BpcPartner,
  ICreateOrganisationRequest,
  IUpdateOrganisationRequest,
  OrganisationWithExtras,
} from 'interfaces/IOrganisations';
import { CHECK_EMAIL_PATH } from 'constants/routeConstants';
import {
  AU_STATES,
  ORGANISATION_ADDRESS1_REQUIRED_MESSAGE,
  ORGANISATION_CITY_REQUIRED_MESSAGE,
  ORGANISATION_COUNTRY_REQUIRED_MESSAGE,
  ORGANISATION_FIRST_NAME_REQUIRED_MESSAGE,
  ORGANISATION_LAST_NAME_REQUIRED_MESSAGE,
  ORGANISATION_NAME_MAX_LENGTH_MESSAGE,
  ORGANISATION_NAME_REQUIRED_MESSAGE,
  ORGANISATION_POSTCODE_REQUIRED_MESSAGE,
  ORGANISATION_STATE_REQUIRED_MESSAGE,
} from 'constants/uiConstants';
import { UNIQUE_DEVICE_ID } from 'constants/authConstants';
import {
  EMAIL_REGEX,
  MOBILE_REGEX,
  ORG_PHONE_REGEX,
  ORGANISATION_NAME_MAX_LENGTH,
} from 'constants/validationConstants';

const normalizePhone = (value: string) => value?.replace(/[^0-9+\s]/g, '');

const makePhoneValidator = (regex: RegExp, message: string) => ({
  validator: (_: unknown, value: string | undefined) => {
    if (!value) return Promise.resolve();
    if (regex.test(value.replace(/\s/g, ''))) return Promise.resolve();
    return Promise.reject(new Error(message));
  },
});

const emailValidator = {
  validator: (_: unknown, value: string | undefined) => {
    if (!value || EMAIL_REGEX.test(value)) return Promise.resolve();
    return Promise.reject(new Error('Enter a valid email address'));
  },
};

const fieldLabel = (text: string, optional = false) => (
  <>
    <span className="text-label font-bold text-gray-dark">
      {text}
      {!optional && <span className="ml-0.5 text-danger">*</span>}
    </span>
    {optional && (
      <span className="text-label mr-1 font-normal text-secondary-dark">
        Optional
      </span>
    )}
  </>
);

interface IOrganisationFormValues {
  orgName: string;
  address1: string;
  address2?: string;
  city: string;
  state?: string;
  postcode: string;
  country: string;
  firstName: string;
  lastName: string;
  preferredName?: string;
  mobile?: string;
  phone?: string;
  email?: string;
  notes?: string;
}

interface IOrganisationCreateEditModalProps {
  mode: 'create' | 'edit';
  open: boolean;
  onClose: () => void;
  organisation?: OrganisationWithExtras;
}

const OrganisationCreateEditModal = ({
  mode,
  open,
  onClose,
  organisation,
}: IOrganisationCreateEditModalProps) => {
  const [form] = Form.useForm();
  const [apiError, setApiError] = useState<string | null>(null);
  const [isFormChanged, setIsFormChanged] = useState(false);

  const navigate = useNavigate();
  const { data: userData } = useFetchUserProfile();
  const createMutation = useCreateOrganisation();
  const updateMutation = useUpdateOrganisation();

  const isPending = createMutation.isPending || updateMutation.isPending;
  const isCreate = mode === 'create';

  useEffect(() => {
    if (!open) return;

    setApiError(null);
    setIsFormChanged(false);
    form.resetFields();

    if (!isCreate && organisation) {
      form.setFieldsValue({
        orgName: organisation.orgName ?? '',
        address1: organisation.address1 ?? '',
        address2: organisation.address2 ?? '',
        city: organisation.city ?? '',
        state: organisation.state || undefined,
        postcode: organisation.postcode ?? '',
        country: organisation.country ?? '',
        firstName: organisation.firstName ?? '',
        lastName: organisation.lastName ?? '',
        preferredName: organisation.preferredName ?? '',
        mobile: organisation.mobile ?? '',
        phone: organisation.phone ?? '',
        email: organisation.email ?? '',
        notes: organisation.notes ?? '',
      });
    }
  }, [open, isCreate, organisation]);

  const handleFinish = (values: IOrganisationFormValues) => {
    if (!userData || !isUuid(userData.id)) {
      navigate(CHECK_EMAIL_PATH);
      return;
    }

    setApiError(null);

    const trimmed = {
      orgName: values.orgName.trim(),
      address1: values.address1.trim(),
      address2: values.address2?.trim(),
      city: values.city.trim(),
      state: values.state,
      postcode: values.postcode.trim(),
      country: values.country.trim(),
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      preferredName: values.preferredName?.trim(),
      mobile: values.mobile?.replace(/\s/g, ''),
      phone: values.phone?.replace(/\s/g, ''),
      email: values.email?.trim(),
      notes: values.notes?.trim(),
    };

    if (isCreate) {
      const newId = uuidv4();
      const payload: ICreateOrganisationRequest = {
        ...trimmed,
        id: newId,
        orgId: newId,
        sourceId: UNIQUE_DEVICE_ID,
        isActive: true,
        partner: BpcPartner.Unspecified,
        orgSetting: { startMonth: 1, nozzles: [], waterRate: null },
        userAccesses: [{ userId: userData.id, access: 15 }],
      };

      createMutation.mutate(payload, {
        onSuccess: () => {
          onClose();
        },
        onError: () => {
          setApiError('Unable to create organisation. Please try again.');
        },
      });
    } else if (organisation) {
      const payload: IUpdateOrganisationRequest = {
        ...trimmed,
        id: organisation.id,
        orgId: organisation.orgId,
        sourceId: UNIQUE_DEVICE_ID,
        isActive: organisation.isActive,
        partner: organisation.partner,
        partnerId: organisation.partnerId,
        orgSetting: organisation.orgSetting,
        userAccesses: organisation.userAccesses,
        fax: organisation.fax,
        abn: organisation.abn,
      };

      updateMutation.mutate(payload, {
        onSuccess: () => {
          onClose();
        },
        onError: () => {
          setApiError('Unable to save organisation. Please try again.');
        },
      });
    }
  };

  const footer = (
    <div className="flex justify-end gap-3 pr-2">
      <Button
        variant="secondary"
        className="px-4 py-1 text-base font-semibold"
        onClick={onClose}
        disabled={isPending}
        type="button"
      >
        Cancel
      </Button>
      <Button
        variant="success"
        className="px-4 py-1 text-base font-semibold"
        disabled={(!isCreate && !isFormChanged) || isPending}
        onClick={() => form.submit()}
        type="button"
      >
        {isPending
          ? isCreate
            ? 'Creating...'
            : 'Saving changes...'
          : isCreate
            ? 'Create'
            : 'Save changes'}
      </Button>
    </div>
  );

  return (
    <Modal
      title={
        <span className="ml-1 text-lg font-semibold text-gray-dark">
          {isCreate ? 'Create Organisation' : 'Edit Organisation'}
        </span>
      }
      open={open}
      onCancel={onClose}
      footer={footer}
      mask={{ closable: !isPending }}
      keyboard={!isPending}
      closable={!isPending}
      width={680}
      className="!max-w-[calc(100vw-2rem)]"
      classNames={{
        root: '!rounded-2xl',
        header: '!mb-0',
        body: '!p-1 !pr-2 !max-h-[80vh] !overflow-y-auto !overflow-x-hidden',
      }}
      wrapClassName="org-modal-wrap"
      destroyOnHidden={false}
    >
      <p className="text-form-hint mb-2 font-normal text-secondary-dark">
        An asterisk (*) indicates required fields
      </p>

      {apiError && (
        <Alert
          message={apiError}
          type="error"
          className="mb-4"
          closable
          onClose={() => setApiError(null)}
        />
      )}

      <ConfigProvider
        theme={{ components: { Form: { verticalLabelPadding: '0 0 0.25rem' } } }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          onValuesChange={() => setIsFormChanged(true)}
          requiredMark={false}
          colon={false}
          disabled={isPending}
          className="org-modal-form"
          scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
        >
          <Form.Item
            name="orgName"
            label={fieldLabel('Organisation name')}
            className="!mb-3"
            rules={[
              { required: true, message: ORGANISATION_NAME_REQUIRED_MESSAGE },
              {
                max: ORGANISATION_NAME_MAX_LENGTH,
                message: ORGANISATION_NAME_MAX_LENGTH_MESSAGE,
              },
            ]}
          >
            <Input maxLength={ORGANISATION_NAME_MAX_LENGTH} showCount />
          </Form.Item>

          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item
              name="address1"
              label={fieldLabel('Address line 1')}
              className="!mb-3"
              rules={[
                {
                  required: true,
                  message: ORGANISATION_ADDRESS1_REQUIRED_MESSAGE,
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="address2"
              label={fieldLabel('Address line 2', true)}
              className="!mb-3"
            >
              <Input />
            </Form.Item>
          </div>

          <div className="grid grid-cols-4 gap-x-3">
            <Form.Item
              name="city"
              label={fieldLabel('City')}
              className="!mb-3"
              rules={[
                { required: true, message: ORGANISATION_CITY_REQUIRED_MESSAGE },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="state"
              label={fieldLabel('State')}
              className="!mb-3"
              rules={[
                {
                  required: true,
                  message: ORGANISATION_STATE_REQUIRED_MESSAGE,
                },
              ]}
            >
              <Select
                options={AU_STATES}
                showSearch
                optionFilterProp="label"
                getPopupContainer={(trigger) =>
                  trigger.parentElement ?? document.body
                }
              />
            </Form.Item>

            <Form.Item
              name="postcode"
              label={fieldLabel('Postcode')}
              className="!mb-3"
              rules={[
                {
                  required: true,
                  message: ORGANISATION_POSTCODE_REQUIRED_MESSAGE,
                },
              ]}
            >
              <Input inputMode="numeric" />
            </Form.Item>

            <Form.Item
              name="country"
              label={fieldLabel('Country')}
              className="!mb-3"
              rules={[
                {
                  required: true,
                  message: ORGANISATION_COUNTRY_REQUIRED_MESSAGE,
                },
              ]}
            >
              <Input />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item
              name="firstName"
              label={fieldLabel('First name')}
              className="!mb-3"
              rules={[
                {
                  required: true,
                  message: ORGANISATION_FIRST_NAME_REQUIRED_MESSAGE,
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="lastName"
              label={fieldLabel('Last name')}
              className="!mb-3"
              rules={[
                {
                  required: true,
                  message: ORGANISATION_LAST_NAME_REQUIRED_MESSAGE,
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="preferredName"
              label={fieldLabel('Preferred name', true)}
              className="!mb-3"
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="mobile"
              label={fieldLabel('Mobile phone', true)}
              className="!mb-3"
              normalize={normalizePhone}
              rules={[
                makePhoneValidator(
                  MOBILE_REGEX,
                  'Enter a valid mobile number (e.g. 0412 345 678)'
                ),
              ]}
            >
              <Input inputMode="tel" />
            </Form.Item>

            <Form.Item
              name="phone"
              label={fieldLabel('Alternate phone', true)}
              className="!mb-3"
              normalize={normalizePhone}
              rules={[
                makePhoneValidator(
                  ORG_PHONE_REGEX,
                  'Enter a valid phone number'
                ),
              ]}
            >
              <Input inputMode="tel" />
            </Form.Item>

            <Form.Item
              name="email"
              label={fieldLabel('Email', true)}
              className="!mb-3"
              rules={[emailValidator]}
            >
              <Input inputMode="email" />
            </Form.Item>
          </div>

          <Form.Item
            name="notes"
            label={fieldLabel('Notes', true)}
            className="!mb-0"
          >
            <Input.TextArea rows={3} maxLength={1000} />
          </Form.Item>
        </Form>
      </ConfigProvider>
    </Modal>
  );
};

export default OrganisationCreateEditModal;
